import { i18n } from '@canvas/intl';

import { shortcutMessages } from './i18n.ts';
import {
  type ShortcutSequence,
  type PrintKind,
  type MacModifier,
  type NonMacModifier,
  type OperatingSystem,
  type Shortcut,
} from './shortcuts-types.ts';
import { sortModifiers } from './shortcuts-utils.ts';

export class ShortcutPrinter<T extends string> {
  print(os: OperatingSystem, sequence: ShortcutSequence) {
    if (os === 'mac') {
      return this.printMac(sequence, 'short');
    } else {
      return this.printNonMac(sequence, 'short');
    }
  }

  printVerbose(os: OperatingSystem, sequence: ShortcutSequence) {
    if (os === 'mac') {
      return this.printMac(sequence, 'verbose');
    } else {
      return this.printNonMac(sequence, 'verbose');
    }
  }

  printKeyMap(os: OperatingSystem, shortcut: Shortcut<T>): string[] {
    if (os === 'mac') {
      return this.printMacRaw(shortcut.sequence);
    } else {
      return this.printNonMacRaw(shortcut.sequence);
    }
  }

  public printMac(shortcut: ShortcutSequence, kind: PrintKind = 'short') {
    const chars: string[] = [];
    for (const modifier of shortcut.macOS.modifiers) {
      chars.push(this.macModifierToString(modifier, kind));
    }

    chars.push(...shortcut.macOS.characters.map((msg) => i18n.intl.formatMessage(msg)));

    return chars;
  }

  public printNonMac(shortcut: ShortcutSequence, kind: PrintKind = 'short') {
    const chars: string[] = [];
    for (const modifier of shortcut.other.modifiers) {
      chars.push(this.nonMacModifierToString(modifier, kind));
    }

    chars.push(...shortcut.other.characters.map((msg) => i18n.intl.formatMessage(msg)));

    return chars;
  }

  private macModifierToString(modifier: MacModifier, kind: PrintKind = 'short') {
    const isVerbose = kind === 'verbose';
    switch (modifier) {
      case 'cmd': {
        return isVerbose
          ? i18n.intl.formatMessage(
              shortcutMessages['whiteboards.shortcuts.verbose.command.do-not-translate'],
            )
          : i18n.intl.formatMessage(
              shortcutMessages['whiteboards.shortcuts.short.cmd.do-not-translate'],
            );
      }
      case 'shift': {
        return isVerbose
          ? i18n.intl.formatMessage(
              shortcutMessages['whiteboards.shortcuts.verbose.shift.do-not-translate'],
            )
          : i18n.intl.formatMessage(
              shortcutMessages['whiteboards.shortcuts.short.shift.do-not-translate'],
            );
      }
      case 'option': {
        return isVerbose
          ? i18n.intl.formatMessage(
              shortcutMessages['whiteboards.shortcuts.verbose.option.do-not-translate'],
            )
          : i18n.intl.formatMessage(
              shortcutMessages['whiteboards.shortcuts.short.option.do-not-translate'],
            );
      }
    }
  }

  private nonMacModifierToString(modifier: NonMacModifier, kind: PrintKind = 'short') {
    const isVerbose = kind === 'verbose';
    switch (modifier) {
      case 'ctrl': {
        return isVerbose
          ? i18n.intl.formatMessage(
              shortcutMessages['whiteboards.shortcuts.verbose.control.do-not-translate'],
            )
          : i18n.intl.formatMessage(
              shortcutMessages['whiteboards.shortcuts.short.ctrl.do-not-translate'],
            );
      }
      case 'shift': {
        return i18n.intl.formatMessage(
          shortcutMessages['whiteboards.shortcuts.verbose.shift.do-not-translate'],
        );
      }
      case 'alt': {
        return isVerbose
          ? i18n.intl.formatMessage(
              shortcutMessages['whiteboards.shortcuts.verbose.alt.do-not-translate'],
            )
          : i18n.intl.formatMessage(
              shortcutMessages['whiteboards.shortcuts.short.alt.do-not-translate'],
            );
      }
    }
  }

  private printMacRaw(shortcut: ShortcutSequence) {
    const chars: string[] = [];
    // Sort modifiers to match the order produced by sortModifiers() in the input package,
    // so the keymap lookup key is consistent with the key sequence built at event time.
    const sortedModifiers = sortModifiers(
      shortcut.macOS.modifiers.map(this.macModifierToStringRaw),
    );
    chars.push(...sortedModifiers);

    // We lowercase here as the input package assumes lowercased raw keys
    chars.push(...shortcut.macOS.raw.map((key) => key.toLowerCase()));
    return chars;
  }

  private printNonMacRaw(shortcut: ShortcutSequence) {
    const chars: string[] = [];
    // Sort modifiers so the keymap lookup key is consistent with
    // the key sequence built at event time
    const sortedModifiers = sortModifiers(
      shortcut.other.modifiers.map(this.nonMacModifierToStringRaw),
    );
    chars.push(...sortedModifiers);

    chars.push(...shortcut.other.raw.map((key) => key.toLowerCase()));
    return chars;
  }

  private macModifierToStringRaw = (modifier: MacModifier) => {
    switch (modifier) {
      case 'cmd': {
        return 'Command';
      }
      case 'shift': {
        return 'Shift';
      }
      case 'option': {
        return 'Alt';
      }
    }
  };

  private nonMacModifierToStringRaw = (modifier: NonMacModifier) => {
    switch (modifier) {
      case 'ctrl': {
        return 'Control';
      }
      case 'shift': {
        return 'Shift';
      }
      case 'alt': {
        return 'Alt';
      }
    }
  };
}
